import React from "react";
import Media from "@qlik/qlik/src/components/Media";
import closeIcon from "@qlik/qlik/src/assets/images/search-close.svg";
import logo from "@qlik/qlik/src/assets/images/Qlik_logo.svg";
import coverImageTop from "@qlik/qlik/src/assets/images/search-left-top.png";
import coverImagebottom from "@qlik/qlik/src/assets/images/search-right-bottom.png";
import * as styles from "./styles.module.css";
import { useI18n } from "@qlik/utils/i18nContext";
import { getMicrocopyValueFromKeys, renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";

/**
 * SearchPopup component
 * This component is used to display a search popup with a close button, logo, search input, and search button.
 * @param {Function} setIsSearchOpen - The function to set the state of the search popup.
 */
const SearchPopup = ({ setIsSearchOpen, searchPopupData }) => {
    // Grab the i18n helper.
    const { i18n } = useI18n();

    const [
        closeIconText,
        tagLine,
        searchPlaceholderText,
        searchBtnText,
        maintenanceText,
        maintenanceMode,
    ] = getMicrocopyValueFromKeys(searchPopupData, [
        "search-popup.close-button-text",
        "search-popup.title-text",
        "search-popup.placeholder-text",
        "search-popup.search-button-text",
        "search-popup.maintenance-text",
        "search-popup.maintenance-mode",
    ]);

    // create boolean isMaintenanceMode based on the value of maintenanceMode
    // if maintenanceMode is "On" or "True" (case-insensitive), isMaintenanceMode will be true
    // if maintenanceMode is not "On" or "True" (case-insensitive), or even undefined or null, isMaintenanceMode will be false
    const isMaintenanceMode = maintenanceMode?.toLowerCase() === "on" || maintenanceMode?.toLowerCase() === "true";

    return (
        <div className={`${styles.searchPopup} fixed top-0 bottom-0 left-0 right-0 bg-white z-10`}>
            {/* Close button */}
            <button
                type="button"
                onClick={() => setIsSearchOpen(false)}
                className="absolute top-10.25 right-10.25"
                aria-label={closeIconText}
            >
                <Media link={closeIcon} />
            </button>
            <span className="sr-only">{closeIconText}</span>
            {/* Cover image at the top */}
            <div className="cover-image-top pointer-events-none">
                <Media link={coverImageTop} width="938px" height="176px" />
            </div>
            {/* Main content */}
            <div className="flex flex-col m-auto items-center h-full justify-center  max-w-80 md:max-w-none">
                {/* Logo */}
                <Media link={logo} className="m-auto w-[175px]" />
                <h2 className="text-center mt-15 mb-7.5">{tagLine}</h2>
                {/* Search input and button */}
                {isMaintenanceMode ? (
                    maintenanceText && (
                        <p
                            className={"maintenance-text text-center max-w-xl"}
                            dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(maintenanceText) }}
                        ></p>
                    )
                ) : (
                    <form
                        method="get"
                        action={i18n.getLangPrefixedPath("/search")}
                        className="flex  flex-col md:flex-row justify-center items-center gap-3.75"
                    >
                        <input
                            type="text"
                            name="searchString"
                            className="border border-stone rounded min-h-15 md:min-w-[559px] bg-search bg-no-repeat py-2.5 px-15 outline-none"
                            placeholder={searchPlaceholderText}
                        />
                        <button
                            type="submit"
                            className="btn-primary-large tracking-normal max-h-[54px]"
                            aria-label={searchBtnText}
                        >
                            {searchBtnText}
                        </button>
                        <span className="sr-only">{searchBtnText}</span>
                    </form>
                )}
            </div>
            {/* Cover image at the bottom */}
            <div className="cover-image-bottom pointer-events-none">
                <Media link={coverImagebottom} width="864px" height="223px" />
            </div>
        </div>
    );
};

export default SearchPopup;
